// This file is a placeholder for creating a Redux store.
// Add your Redux store configuration and setup here.
import React, { useEffect, useState } from "react";
import DropZone from "../components/ui/DropZone"; // Corrected relative path
import Input from "../components/ui/Input"; // Corrected relative path (was TextInput)
import SelectBox from "../components/ui/SelectBox"; // Corrected relative path
import Button from "../components/ui/Button"; // Corrected relative path (was Btn)
import FastAPIClient from "../services/apiClient"; // Corrected import path for our client
import config from "../config"; // Our config for base API path

import { fetchCurrencies, API_BASE_PATH, API_ROOT_PATH } from "../apiHelper"; // Assuming fetchCurrencies still works as is
import { Image } from 'lucide-react'; // Import the Image icon from lucide-react
import { useNavigate } from "react-router-dom";

// Initialize the API client with our config
const client = new FastAPIClient();

/**
 * CreateStore Component
 * Allows users to create new store entries by providing store details,
 * address information, and uploading a store photo.
 * Features client-side state management, API interaction, and a live image preview.
 * Designed to be nested within the main application Scaffold layout.
 */
const CreateStore = () => {
    const [storeObject, setStoreObject] = useState({
        // Initialize with default values for a new store as per schema
        store_id: 0, // Will likely be ignored/auto-generated by backend for new create
        member_id: 0, // As per schema for creation
        setccurr: '',
        identifier: '',
        storetype: '', // This will now hold the selected business type value (e.g., 'RTL')
        staddress_id: 0, // As per schema for creation
        // REMOVED: businesstype

        // Address fields (will be part of staddress payload)
        address1: '',
        address2: '',
        zipcode: '',
        city: '',
        state: '',
        country: '',
        email1: '',
        phone1: '',
        firstname: '',
        lastname: '',
        persontitle: '',
        nickname: '', // Often derived from identifier or a separate field

        // Attachment fields (for the attachment payload)
        storeimage_url: '',
        filename: '',
        mimetype: '',
        mimetypeencoding: '',
        filesize: 0,
    });
    const [user, setUser] = useState({});
    const [toast, showToast] = useState({ message: '', show: false, toastType: 1 }); // For notifications
    const [currencies, setCurrencies] = useState([]); // For currency options
    const navigate = useNavigate()

    // Effect to load user and currencies on component mount
    useEffect(() => {
        // Load user from local storage
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            // Potentially redirect to login if no user is found
            console.warn("No user found in local storage. Consider redirecting to login.");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
            setStoreObject(prev => ({ ...prev, member_id: JSON.parse(lcUser).users_id }));
        }

        // Load currencies for the SelectBox
        const loadCurrencies = async () => {
            try {
                const fetchedCurrencies = await fetchCurrencies();
                const currencyOptions = fetchedCurrencies.map((currency) => ({
                    label: `${currency.setccurr} - ${currency.setcnote}`,
                    value: `${currency.setccurr}`
                }));
                setCurrencies(currencyOptions);
            } catch (err) {
                console.error("Error loading currencies:", err);
                showToast({ ...toast, message: err.response?.data?.detail || "Failed to load currencies.", show: true, toastType: 0 });
            }
        };
        loadCurrencies();
    }, []); // Empty dependency array means this runs once on mount

    // Handler for all text and select input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Special handling for 'identifier' to also update 'nickname' if they are linked
        if (name === "identifier") {
            setStoreObject((prevObject) => ({
                ...prevObject,
                nickname: value,
                [name]: value, // Also update identifier itself
            }));
        } else {
            setStoreObject((prevObject) => ({
                ...prevObject,
                [name]: value,
            }));
        }
    };

    // Handler for DropZone's upload complete event
    const handleImageUploadComplete = (uploadedFiles) => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            const fileData = uploadedFiles[0];
            const { url, filename, mimetype, encoding, size } = fileData;
            setStoreObject((prevObject) => ({
                ...prevObject,
                storeimage_url: url,
                filename: filename,
                mimetype: mimetype,
                mimetypeencoding: encoding,
                filesize: size,
            }));
        }
    };

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Construct the payload according to the new creation schema
        const payload = {
            store: {
                member_id: storeObject.member_id, // Default to 0, or user.member_id if derived from user
                setccurr: storeObject.setccurr,
                identifier: storeObject.identifier,
                storetype: storeObject.storetype, // This now holds the selected business type value
                staddress_id: storeObject.staddress_id, // Default to 0
                // REMOVED: businesstype from payload
            },
            staddress: {
                staddress_id: storeObject.staddress_id, // Default to 0
                address1: storeObject.address1,
                address2: storeObject.address2,
                member_id: storeObject.member_id, // Default to 0, or user.member_id
                zipcode: storeObject.zipcode,
                city: storeObject.city,
                state: storeObject.state,
                country: storeObject.country,
                email1: storeObject.email1,
                phone1: storeObject.phone1,
                firstname: storeObject.firstname,
                lastname: storeObject.lastname,
                persontitle: storeObject.persontitle,
                nickname: storeObject.nickname, // Use nickname from state
            },
            attachment: {
                attachmenturl: storeObject.storeimage_url,
                member_id: storeObject.member_id, // Default to 0, or user.member_id
                attachmentusage_id: "StorePhoto", // Specific string for store photos
                mimetype: storeObject.mimetype,
                mimetypeencoding: storeObject.mimetypeencoding,
                filename: storeObject.filename,
                filesize: storeObject.filesize,
            }
        };

        // console.log('Attempting to create store with payload:', payload);
        try {
            // Using our FastAPIClient's postData method
            const response = await client.postData(`${API_BASE_PATH}/store/create-store`, payload);
            // console.log('Branch created successfully:', response);
            showToast({ message: "Branch created successfully!", show: true, toastType: 1 });
            // The fix: Reset all fields except for the `member_id`
            setStoreObject(prev => ({
                ...prev, // Spread previous state to preserve member_id
                store_id: 0, // Not needed for create, but good to reset
                setccurr: '',
                identifier: '',
                storetype: '', // Reset storetype (business type)
                staddress_id: 0, // Not needed for create, but good to reset
                address1: '',
                address2: '',
                zipcode: '',
                city: '',
                state: '',
                country: '',
                email1: '',
                phone1: '',
                firstname: '',
                lastname: '',
                persontitle: '',
                nickname: '',
                storeimage_url: '',
                filename: '',
                mimetype: '',
                mimetypeencoding: '',
                filesize: 0,
            }));
        } catch (err) {
            console.error("Failed to create branch:", err);

            // Extract error detail from response or customMessage
            const detail = err.response?.data?.detail;
            let errorMessage = "Failed to create branch.";

            if (Array.isArray(detail)) {
                errorMessage = detail.map(d => `${d.loc ? d.loc[d.loc.length - 1] + ': ' : ''}${d.msg}`).join(' | ');
            } else if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (err.customMessage) {
                errorMessage = err.customMessage;
            } else if (err.message) {
                errorMessage = err.message;
            }

            showToast({ message: errorMessage, show: true, toastType: 0 });
        }
    };

    // DELETED: storeTypeOptions array (unused)

    // Renamed the variable for clarity, but the options remain the same
    const businessTypeOptions = [
        { label: "Retail: RTL", value: "RTL" },
        { label: "Warehouse: WHS", value: "WHS" },
        { label: "Education: EDU", value: "EDU" },
        { label: "Others: OTR", value: "OTR" },
    ];


    return (
        // Removed min-h-screen, bg-gray-100, py-8 as Scaffold's main handles these
        <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Form */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Create New Branch</h1>
                        <p className="text-gray-600 text-md">Fill in the details below to add a new business branch to your organization.</p>
                    </div>

                    {/* Toast Notification */}
                    {toast.show && (
                        <div className={`p-3 mb-4 rounded-md text-sm font-medium ${toast.toastType === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {toast.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Basic Store Information */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Branch Details</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input // Changed from TextInput
                                    name="identifier"
                                    value={storeObject.identifier}
                                    onChange={handleInputChange}
                                    label="Branch Name / Identifier"
                                    type="text"
                                    id="identifier"
                                    placeholder="e.g., Main Branch, Location A"
                                    required={true}
                                />
                                <SelectBox
                                    name="storetype" // *** TARGET VARIABLE: storetype ***
                                    value={storeObject.storetype}
                                    onChange={handleInputChange}
                                    label="Business Type"
                                    required={true}
                                    options={businessTypeOptions}
                                    placeholder="Select business type"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* The original currency select box */}
                                <SelectBox
                                    name="setccurr"
                                    value={storeObject.setccurr}
                                    onChange={handleInputChange}
                                    label="Default Currency"
                                    required={true}
                                    options={currencies}
                                    placeholder="Select currency"
                                />
                                {/* Placeholder to fill the space if needed, or remove this div if the layout should collapse */}
                                <div className="hidden md:block"></div>
                            </div>
                        </div>

                        {/* Branch Photo Upload (unchanged) */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Branch Photo</h2>
                            <DropZone
                                label="Drag and drop your descriptive branch photo here or click to upload (JPG, PNG)"
                                accept=".jpg,.png,.jpeg"
                                uploadUrl={`${API_BASE_PATH}/uploads/upload`}
                                onUploadComplete={handleImageUploadComplete}
                            />
                        </div>

                        {/* Address Information (unchanged) */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Address Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input name="address1" value={storeObject.address1} onChange={handleInputChange} label="Address Line 1" type="text" id="address1" placeholder="Street address, P.O. box" />
                                <Input name="address2" value={storeObject.address2} onChange={handleInputChange} label="Address Line 2" type="text" id="address2" placeholder="Apartment, suite, unit, building" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Input name="city" value={storeObject.city} onChange={handleInputChange} label="City" type="text" id="city" placeholder="City" />
                                <Input name="state" value={storeObject.state} onChange={handleInputChange} label="State / Province" type="text" id="state" placeholder="State or Province" />
                                <Input name="zipcode" value={storeObject.zipcode} onChange={handleInputChange} label="Zip / Postal Code" type="text" id="zipcode" placeholder="Zip or Postal Code" />
                            </div>
                            <div className="mb-4">
                                <Input name="country" value={storeObject.country} onChange={handleInputChange} label="Country" type="text" id="country" placeholder="Country" />
                            </div>
                        </div>

                        {/* Contact Person Information (unchanged) */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Contact Person</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input name="email1" value={storeObject.email1} onChange={handleInputChange} label="Contact Email" type="email" id="email1" placeholder="contact@example.com" />
                                <Input name="phone1" value={storeObject.phone1} onChange={handleInputChange} label="Contact Phone" type="tel" id="phone1" placeholder="+1 (555) 123-4567" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Input name="persontitle" value={storeObject.persontitle} onChange={handleInputChange} label="Title" type="text" id="persontitle" placeholder="Mr., Ms., Dr., Chief" />
                                <Input name="firstname" value={storeObject.firstname} onChange={handleInputChange} label="First Name" type="text" id="firstname" placeholder="Contact person's first name" />
                                <Input name="lastname" value={storeObject.lastname} onChange={handleInputChange} label="Last Name" type="text" id="lastname" placeholder="Contact person's last name" />
                            </div>
                        </div>

                        {/* Submit Button (unchanged) */}
                        <div className="flex justify-end pt-4">
                            <Button // Changed from Btn
                                type="submit"
                                variant="primary" // Explicitly use primary variant
                                size="lg" // Larger button for submission
                                className="flex items-center justify-center" // Ensure flex for icon+text
                            >
                                <svg className="w-5 h-5 text-white mr-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14M5 12h14" />
                                </svg>
                                Create Branch
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Right Column: Live Preview / Information Panel (unchanged) */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="p-4 mt-8 border border-gray-200 rounded-2xl w-full max-w-sm mx-auto flex flex-col items-center justify-between text-center">
                        <div className="w-32 h-32 relative mx-auto -mt-16 mb-4 border-4 border-white rounded-xl shadow-lg flex items-center justify-center bg-gray-100">
                            {storeObject.storeimage_url ? (
                                <img
                                    src={`${API_ROOT_PATH}${storeObject.storeimage_url}`} // Use config.apiBasePath for displaying image
                                    alt="Store Photo Preview"
                                    className="w-full h-full object-cover rounded-xl"
                                />
                            ) : (
                                <Image size={64} className="text-gray-400" /> // Replaced problematic SVG with Lucide Image icon
                            )}
                        </div>

                        <div className="flex flex-col mb-4 items-center gap-1">
                            <h2 className="font-bold text-gray-700 text-lg">New Branch Name</h2>
                            <p className="text-gray-500 text-center text-md">
                                {storeObject.identifier || "Your Branch Name Here"}
                            </p>
                        </div>
                        <div className="flex flex-col mb-4 items-center gap-1">
                            <h2 className="font-bold text-gray-700 text-lg">Contact & Address</h2>
                            <p className="text-gray-500 text-center text-sm">
                                {storeObject.address1 ? [
                                    storeObject.address1,
                                    storeObject.address2,
                                    storeObject.city,
                                    storeObject.state,
                                    storeObject.zipcode,
                                    storeObject.country
                                ].filter(Boolean).join(', ') : "Address will appear here"}
                                {storeObject.email1 && `\nEmail: ${storeObject.email1}`}
                                {storeObject.phone1 && `\nPhone: ${storeObject.phone1}`}
                            </p>
                            <p className="text-gray-500 text-center text-sm mt-2">
                                {storeObject.firstname || storeObject.lastname ? `Contact: ${[storeObject.persontitle, storeObject.firstname, storeObject.lastname].filter(Boolean).join(' ')}` : "Contact person details"}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateStore;